/**
 * OSMMapProvider - OpenStreetMap/Leaflet implementation
 *
 * Wraps Leaflet (OSM) in the MapProvider interface.
 */
import { MapProvider } from './MapProvider.js';
import { FullWindowControl } from '../controls/FullWindowControl.js';
import { MapTypeControl } from '../controls/MapTypeControl.js';
import { createMarkerIcon } from '../MarkerUtils.js';

export class OSMMapProvider extends MapProvider {
	constructor(elementId, options = {}) {
		super(elementId, options);
		this.mapType = 'osm';
		this.tileLayers = {};
		this.currentLayer = null;
		this.currentLayerType = 'roadmap';
		this.oms = null; // OverlappingMarkerSpiderfier instance
	}

	/**
	 * Initialize Leaflet map
	 */
	init() {
		if (!window.L || !L.version) {
			return false;
		}

		// Get map element
		const mapElement = document.getElementById(this.elementId);
		if (!mapElement) {
			return false;
		}

		// Create map directly with Leaflet API
		this.map = L.map(this.elementId, {
			center: [
				parseFloat(this.options.latitude) || 0,
				parseFloat(this.options.longitude) || 0
			],
			zoom: parseInt(this.options.zoom) || 12,
			scrollWheelZoom: this.options.scrollwheel !== false,
			zoomControl: this.options.zoomControl !== false,
			attributionControl: false, // Remove attribution control
			...this.options.mapOptions
		});

		// Map type conversion - OSM only supports roadmap and satellite
		let osmMapType = 'roadmap';
		if (this.options.maptype) {
			const mapTypeLower = this.options.maptype.toLowerCase();
			if (['roadmap', 'satellite'].includes(mapTypeLower)) {
				osmMapType = mapTypeLower;
			}
		}

		// Create tile layers for different map types
		this.tileLayers.roadmap = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
			maxZoom: 19,
			attribution: '© OpenStreetMap contributors'
		});

		this.tileLayers.satellite = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
			maxZoom: 19,
			attribution: 'Tiles © Esri'
		});

		// Add initial layer based on maptype option
		this.currentLayer = this.tileLayers[osmMapType];
		this.currentLayerType = osmMapType;
		this.currentLayer.addTo(this.map);

		// Setup tile load handler to hide loading spinner
		this.setupTileLoadHandler(this.currentLayer);

		// Initialize Map Type control (roadmap and satellite only for OSM)
		this.mapTypeControl = new MapTypeControl(this, mapElement, {
			initialType: osmMapType,
			types: ['roadmap', 'satellite']
		});
		this.mapTypeControl.init();

		// Initialize OverlappingMarkerSpiderfier if available
		if (window.OverlappingMarkerSpiderfier) {
			this.oms = new window.OverlappingMarkerSpiderfier(this.map, {
				markersWontMove: true,
				markersWontHide: true,
				basicFormatEvents: true,
				keepSpiderfied: false,
				nearbyDistance: 20
			});
		}

		// Initialize Full Window control
		// Pass both the Leaflet map element (for finding zoom control) and the container (for full screen)
		const fullWindowContainer = mapElement.parentElement;
		if (fullWindowContainer) {
			this.fullWindowControl = new FullWindowControl(this.map, mapElement, fullWindowContainer, {
				hide: this.options.hideExpandMap || false
			});
			this.fullWindowControl.init();
		} else {
		}

		return true;
	}

	/**
	 * Create a marker
	 */
	createMarker(options) {
		const lat = parseFloat(options.lat || options.latitude) || 0;
		const lng = parseFloat(options.lng || options.longitude) || 0;

		const markerOptions = {
			draggable: options.draggable !== false,
			title: options.title || '',
			data: options.data // Store marker data for later retrieval
		};

		// Create icon using shared utility
		const icon = createMarkerIcon(options);
		if (icon) {
			markerOptions.icon = icon;
		}

		const marker = L.marker([lat, lng], markerOptions).addTo(this.map);

		// Add to OverlappingMarkerSpiderfier if available
		if (this.oms) {
			this.oms.addMarker(marker);
		}

		this.markers.push(marker);
		return marker;
	}

	/**
	 * Get center of map
	 */
	getCenter() {
		if (!this.map) return null;
		const center = this.map.getCenter();
		return {
			lat: center.lat,
			lng: center.lng
		};
	}

	/**
	 * Set center of map
	 */
	setCenter(lat, lng) {
		if (!this.map) return;
		this.map.setView([lat, lng]);
	}

	/**
	 * Pan to location
	 */
	panTo(lat, lng) {
		if (!this.map) return;
		this.map.panTo(new L.LatLng(lat, lng));
	}

	/**
	 * Get zoom level
	 */
	getZoom() {
		if (!this.map) return null;
		return this.map.getZoom();
	}

	/**
	 * Set zoom level
	 */
	setZoom(zoom) {
		if (!this.map) return;
		this.map.setZoom(zoom);
	}

	/**
	 * Set map type (roadmap or satellite for OSM)
	 */
	setMapType(type) {
		if (!this.tileLayers[type]) {
			return false;
		}

		if (this.currentLayerType === type) {
			return true;
		}

		// Show loading spinner during map type switch
		this.showLoading();

		// Remove current layer
		if (this.currentLayer && this.map.hasLayer(this.currentLayer)) {
			this.map.removeLayer(this.currentLayer);
		}

		// Add new layer
		this.currentLayer = this.tileLayers[type];
		this.currentLayer.addTo(this.map);
		this.currentLayerType = type;

		// Setup tile load handler to hide loading after new tiles load
		this.setupTileLoadHandler(this.currentLayer);

		return true;
	}

	/**
	 * Get current map type
	 */
	getMapType() {
		return this.currentLayerType;
	}

	/**
	 * Forward geocode using OSM Nominatim
	 */
	async geocode(request) {
		const address = typeof request === 'string' ? request : request.address;
		const countryCode = request.country || '';

		if (!address) {
			throw new Error('Address is required for geocoding');
		}

		try {
			const params = new URLSearchParams({
				q: address,
				format: 'json',
				addressdetails: '1',
				limit: '1'
			});

			if (countryCode) {
				params.append('countrycodes', countryCode.toLowerCase());
			}

			const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
			const results = await response.json();

			return results.map(result => this.normalizeOSMResult(result));
		} catch (error) {
			throw new Error(`OSM Geocoding failed: ${error.message}`);
		}
	}

	/**
	 * Reverse geocode using OSM Nominatim
	 */
	async reverseGeocode(lat, lng) {
		try {
			const params = new URLSearchParams({
				lat: lat.toString(),
				lon: lng.toString(),
				format: 'json',
				addressdetails: '1'
			});

			const response = await fetch(`https://nominatim.openstreetmap.org/reverse?${params}`);
			const result = await response.json();

			return [this.normalizeOSMResult(result)];
		} catch (error) {
			throw new Error(`OSM Reverse geocoding failed: ${error.message}`);
		}
	}

	/**
	 * Normalize OSM result to match Google Maps format
	 */
	normalizeOSMResult(osmResult) {
		const address = osmResult.address || {};

		return {
			display_address: osmResult.display_name,
			lat: parseFloat(osmResult.lat),
			lon: parseFloat(osmResult.lon),
			postcode: address.postcode || '',
			city: address.city || address.town || address.village || '',
			state: address.state || '',
			country: address.country || '',
			country_code: address.country_code ? address.country_code.toUpperCase() : '',
			address: address,
			// GB split support
			gb_country: this.getGBCountry(address)
		};
	}

	/**
	 * Get specific country for GB addresses (England, Scotland, Wales, Northern Ireland)
	 */
	getGBCountry(address) {
		if (address.country_code !== 'gb') return null;

		const state = address.state || '';
		const gbCountries = ['England', 'Northern Ireland', 'Scotland', 'Wales'];

		for (const country of gbCountries) {
			if (state.includes(country)) {
				return country;
			}
		}

		return null;
	}

	/**
	 * Fit map bounds to show all markers
	 * @param {Array} markers - Array of Leaflet marker objects
	 */
	fitBounds(markers) {
		if (!this.map || !markers || markers.length === 0) {
			return;
		}


		// Create Leaflet LatLngBounds
		const bounds = L.latLngBounds();

		markers.forEach(marker => {
			if (marker && marker.getLatLng) {
				bounds.extend(marker.getLatLng());
			}
		});

		this.map.fitBounds(bounds);

		// If there's only one marker, set a reasonable zoom level
		if (markers.length === 1) {
			setTimeout(() => {
				if (this.map.getZoom() > 15) {
					this.map.setZoom(15);
				}
			}, 100);
		}

	}

	/**
	 * Open a popup for a marker
	 */
	openPopup(marker, content) {
		if (!this.map || !marker) {
			return;
		}


		const popup = L.popup({
			maxWidth: 300,
			className: 'geodir-marker-popup'
		}).setContent(content);

		marker.bindPopup(popup);
		marker.openPopup();

	}

	/**
	 * Close any open popup
	 */
	closePopup() {
		if (this.map) {
			this.map.closePopup();
		}
	}

	/**
	 * Add event listener
	 */
	addEventListener(event, callback) {
		if (!this.map) return;

		// Map event names to Leaflet events
		const eventMap = {
			'dragend': 'dragend',
			'zoom_changed': 'zoomend',
			'click': 'click'
		};

		const leafletEvent = eventMap[event] || event;
		this.map.on(leafletEvent, callback);
	}

	/**
	 * Add marker event listener
	 */
	addMarkerListener(marker, event, callback) {
		if (!marker) return;
		marker.on(event, callback);
	}
}
